/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect, useRef, useMemo} from 'react'
import clsx from 'clsx'
import moment from 'moment'
import './InboxChatInnerV3.css'
import InfiniteScroll from 'react-infinite-scroll-component'
import {createFirstLetter} from '../../../../../Utils/_helpers/index'
import {MessageDeliveryStatus} from '../../../reports-analytics/Common'
import {useMessages} from '../../hooks/useMessages'
import {Message} from '../../types/inbox-v3.types'

interface InboxChatInnerV3Props {
  data: string // receiver phone number (for legacy API fallback)
  messengerName: string
  firstTime?: boolean
  /** Conversation ID for the new API */
  conversationId?: string | null
  /** Callback to receive the prependMessage and fetchAndPrependMessage functions for external use (e.g., when sending messages) */
  onMessagesReady?: (helpers: {
    prependMessage: (message: Message) => void
    fetchAndPrependMessage: (messageId: string) => Promise<void>
  }) => void
}

const MessageSkeleton = () => {
  return (
    <div className='d-flex flex-column align-items-start mb-4' style={{maxWidth: '70%'}}>
      {/* Avatar + Name */}
      <div className='d-flex align-items-center mb-2 gap-2'>
        <div
          className='rounded-circle bg-secondary'
          style={{width: 32, height: 32, opacity: 0.2}}
        ></div>
        <div
          className='bg-secondary rounded-pill'
          style={{width: 100, height: 12, opacity: 0.2}}
        ></div>
      </div>

      {/* Message bubble skeleton */}
      <div
        className='bg-secondary rounded-pill'
        style={{
          width: '80%',
          height: 24,
          opacity: 0.2,
        }}
      ></div>

      {/* Timestamp placeholder */}
      <div
        className='bg-secondary rounded'
        style={{width: 120, height: 10, marginTop: 8, opacity: 0.1}}
      ></div>
    </div>
  )
}

const InboxChatInnerV3: FC<InboxChatInnerV3Props> = ({
  data,
  messengerName,
  firstTime = true,
  conversationId,
  onMessagesReady,
}) => {
  const scrollRef = useRef<any>(null)

  // Use the messages hook to fetch real data from API
  // If conversationId is provided, use new API; otherwise fall back to legacy API
  const {
    messages,
    isLoading,
    isFetching,
    hasMore,
    fetchMore,
    prependMessage,
    fetchAndPrependMessage,
  } = useMessages({
    conversationId: conversationId || null,
    autoFetch: true,
    limit: 20,
    pollingInterval: 30000, // Poll every 30 seconds for new messages
  })

  // Expose prependMessage and fetchAndPrependMessage to parent component
  useEffect(() => {
    if (onMessagesReady) {
      onMessagesReady({prependMessage, fetchAndPrependMessage})
    }
  }, [onMessagesReady, prependMessage, fetchAndPrependMessage])

  // Helper to get human-readable date label
  const getDateLabel = (dateStr: string) => {
    const messageDate = moment(dateStr).startOf('day')
    const today = moment().startOf('day')
    const yesterday = moment().subtract(1, 'days').startOf('day')

    if (messageDate.isSame(today)) {
      return 'Today'
    } else if (messageDate.isSame(yesterday)) {
      return 'Yesterday'
    } else {
      return messageDate.format('dddd, MMMM D, YYYY')
    }
  }

  // Group messages by date - memoized for performance
  const groupedMessages = useMemo(() => {
    const grouped: {[key: string]: Message[]} = {}

    messages.forEach((message) => {
      const dateKey = moment(message.createdAt).format('YYYY-MM-DD')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(message)
    })

    // Sort messages within each group by createdAt descending (newest first)
    // With column-reverse, first rendered item appears at visual bottom
    // So newest first in array = newest at visual bottom
    Object.keys(grouped).forEach((dateKey) => {
      grouped[dateKey].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
    })

    return grouped
  }, [messages])

  // Get sorted date keys
  const dateKeys = useMemo(() => {
    return Object.keys(groupedMessages).sort((a, b) => moment(b).diff(moment(a)))
  }, [groupedMessages])

  // Scroll to bottom (newest messages) on initial load and when new messages arrive
  // With column-reverse, scrollTop = 0 shows the newest messages (visual bottom)
  useEffect(() => {
    if (scrollRef.current && firstTime && messages.length > 0 && !isLoading) {
      scrollRef.current.scrollTop = 0
    }
  }, [messages.length, firstTime, isLoading])

  return (
    <div className='inbox-chat-inner'>
      {data !== '' && (
        <>
          <div
            id='scrollableDiv'
            ref={scrollRef}
            style={{
              height: 'calc(100vh - 300px)',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column-reverse',
            }}
          >
            <InfiniteScroll
              dataLength={messages.length}
              next={fetchMore}
              hasMore={hasMore}
              loader={
                <h4 className='text-center text-muted py-2'>
                  {isFetching ? 'Loading more...' : ''}
                </h4>
              }
              style={{display: 'flex', flexDirection: 'column-reverse', overflow: 'hidden'}}
              inverse={true}
              scrollableTarget='scrollableDiv'
            >
              {messages.length > 0 &&
                dateKeys.map((dateKey) => (
                  <React.Fragment key={dateKey}>
                    {/* Messages for this date (sorted oldest to newest for column-reverse display) */}
                    {groupedMessages[dateKey].map((message: Message, index: number) => {
                      // Determine if this is an inbound message (from contact) or outbound (from us)
                      // Inbound: campaign_type is not 'notification' or 'promotional'
                      const isInbound = !['notification', 'promotional'].includes(
                        message?.campaign_type || ''
                      )

                      return (
                        <div
                          key={`message-${message._id || dateKey}-${index}`}
                          className={clsx('d-flex', 'mb-10', {'flex-row-reverse': !isInbound})}
                        >
                          <div
                            className={clsx(
                              'd-flex flex-column align-items',
                              `align-items-${isInbound ? 'start' : 'end'}`
                            )}
                          >
                            {isInbound ? (
                              <>
                                <div className='d-flex align-items-center mb-2'>
                                  <div className='symbol symbol-35px symbol-circle'>
                                    <img src={'/media/avatars/blank.png'} alt='Profile Pic' />
                                  </div>
                                  <div className='ms-3'>
                                    <a className='fs-5 fw-bolder text-gray-900 text-hover-primary me-2'>
                                      {messengerName}
                                    </a>
                                    <div className='fs-7 text-muted mb-1'>{message.sender}</div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className='d-flex flex-row-reverse align-items-center mb-2'>
                                  <div className='symbol symbol-35px symbol-circle'>
                                    {!message?.createdBy?.avatar ||
                                    message?.createdBy?.avatar === 'undefined' ? (
                                      <span className='circle1'>{`${
                                        createFirstLetter(message?.createdBy?.name || '')
                                          ?.firstName ?? 'C'
                                      }${
                                        createFirstLetter(message?.createdBy?.name || '')
                                          ?.lastName ?? 'C'
                                      }`}</span>
                                    ) : (
                                      <img src={message?.createdBy?.avatar} alt='Profile Pic' />
                                    )}
                                  </div>
                                  <div className='me-3'>
                                    <a
                                      href='#'
                                      className='fs-5 fw-bolder text-gray-900 text-hover-primary'
                                    >
                                      {message?.createdBy?.name || 'System'}
                                    </a>
                                    <div className='text-muted fs-7 mb-1'>{message.sender}</div>
                                  </div>
                                </div>
                              </>
                            )}

                            <div
                              className={clsx(
                                'p-2 rounded',
                                `bg-light-${isInbound ? 'info' : 'sender'}`,
                                `text-${isInbound ? 'dark' : 'light'}`,
                                'fw-bold mw-lg-400px',
                                `text-${isInbound ? 'start' : 'end'}`
                              )}
                              data-kt-element='message-text'
                            >
                              {/* Media attachments */}
                              {((message?.media && message.media.length > 0) ||
                                (message?.customerResponseMedia &&
                                  message.customerResponseMedia.length > 0)) && (
                                <>
                                  {[
                                    ...(message?.media || []),
                                    ...(message?.customerResponseMedia || []),
                                  ].map((media, mediaIndex: number) => (
                                    <img
                                      className='m-2 cursor-pointer'
                                      key={mediaIndex}
                                      src={media.url}
                                      width='100px'
                                      height='100px'
                                      alt={`Media ${mediaIndex + 1}`}
                                      onClick={() => {
                                        window.open(media.url, '_blank')
                                      }}
                                    />
                                  ))}
                                  <br />
                                </>
                              )}
                              {message?.message}
                            </div>

                            {/* Timestamp and status */}
                            {isInbound ? (
                              <span
                                className='text-muted fs-7 mb-1'
                                title={`${moment(message?.createdAt)?.format(
                                  'DD/MM/YYYY hh:mm A'
                                )} | ${message?.status}`}
                              >
                                {moment(message?.createdAt)?.fromNow()} (
                                {moment(message?.createdAt)?.format('DD/MM/YYYY hh:mm A')})
                              </span>
                            ) : (
                              <div className='d-flex align-items-center mb-1 gap-2'>
                                <span
                                  title={`${moment(
                                    message?.scheduleAt || message?.createdAt
                                  )?.format('DD/MM/YYYY hh:mm A')} | ${message?.status}`}
                                  className='text-muted fs-7'
                                >
                                  {moment(message?.scheduleAt || message?.createdAt)?.fromNow()} (
                                  {moment(message?.scheduleAt || message?.createdAt)?.format(
                                    'DD/MM/YYYY hh:mm A'
                                  )}
                                  )
                                </span>

                                {/* Status icon */}
                                {(() => {
                                  const status = message?.status

                                  if (
                                    [
                                      MessageDeliveryStatus.DELIVERED,
                                      MessageDeliveryStatus.SEND,
                                    ].includes(status as MessageDeliveryStatus)
                                  ) {
                                    return (
                                      <i
                                        className='bi bi-check-circle text-success'
                                        title={status}
                                      ></i>
                                    )
                                  }

                                  if (status === MessageDeliveryStatus.QUEUED) {
                                    return (
                                      <i className='bi bi-exclamation-circle' title={status}></i>
                                    )
                                  }

                                  if (status === MessageDeliveryStatus.SCHEDULED) {
                                    return <i className='bi bi-clock' title={status}></i>
                                  }

                                  // Fallback for any other status (e.g. FAILED)
                                  return (
                                    <i className='bi bi-x-circle text-danger' title={status}></i>
                                  )
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}

                    {/* Date Separator (appears above messages due to column-reverse) */}
                    <div className='date-separator'>
                      <span className='date-separator-chip'>{getDateLabel(dateKey)}</span>
                    </div>
                  </React.Fragment>
                ))}
            </InfiniteScroll>
          </div>
        </>
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div className='text-center my-3'>
          <div className='px-2'>
            {[...Array(3)].map((_, idx) => (
              <MessageSkeleton key={idx} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {messages.length === 0 && !isLoading && (
        <div className='d-flex flex-column align-items-center justify-content-center h-100 text-muted'>
          <i className='bi bi-chat-dots' style={{fontSize: '48px', marginBottom: '16px'}}></i>
          <p>No messages yet</p>
          <p className='text-center' style={{fontSize: '13px'}}>
            Start the conversation by sending a message
          </p>
        </div>
      )}
    </div>
  )
}

export default InboxChatInnerV3